import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Modal, message, Button } from 'antd';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  DatabaseOutlined,
  UserSwitchOutlined,
  MessageOutlined,
  RobotOutlined,
  BookOutlined,
  ApartmentOutlined,
  BugOutlined,
  ApiOutlined,
  BarChartOutlined,
  ExclamationCircleOutlined,
  SecurityScanOutlined,
  AppstoreOutlined,
  MenuOutlined,
  GiftOutlined,
  DollarOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/Auth/Login';
import PermissionRoute from './components/Auth/PermissionRoute';
import { User, getAccessiblePages, getRoleDisplayName } from './utils/permissions';

// 页面组件
import MemoryManagement from './pages/MemoryManagement';
import UserProfile from './pages/UserProfile';
import ScriptManagement from './pages/ScriptManagement';
import AgentManagement from './pages/AgentManagement';
import KnowledgeBase from './pages/KnowledgeBase';
import WorkflowManagement from './pages/WorkflowManagement';
import DebugTools from './pages/DebugTools';
import ModelManagement from './pages/ModelManagement';
import Reports from './pages/Reports';
import RetentionReports from './pages/RetentionReports';
import Dashboard from './pages/Dashboard';
import AuditLogs from './pages/AuditLogs';
import SystemSettings from './pages/SystemSettings';
import AccountManagement from './pages/AccountManagement';
import RoleManagement from './pages/RoleManagement';
import MenuManagement from './pages/MenuManagement';
import OperationLogs from './pages/OperationLogs';
import ActivityRewards from './pages/ActivityRewards';
import BalanceLogs from './pages/BalanceLogs';
import DomainManagement from './pages/DomainManagement';
// @ts-ignore
import CardManagement from './pages/CardManagement';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const App: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [sessionTimeout, setSessionTimeout] = useState(false);
  const [layoutMode, setLayoutMode] = useState<'sidebar' | 'top'>('sidebar');
  const navigate = useNavigate();
  const location = useLocation();

  // 检查登录状态
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const loginTime = localStorage.getItem('loginTime');
    
    if (savedUser && loginTime) {
      const userData = JSON.parse(savedUser);
      const timeDiff = Date.now() - parseInt(loginTime);
      
      // 检查会话是否超时（2小时）
      if (timeDiff > 2 * 60 * 60 * 1000) {
        handleLogout();
        setSessionTimeout(true);
      } else {
        setUser(userData);
      }
    }
  }, []);

  // 会话超时检查
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      const loginTime = localStorage.getItem('loginTime');
      if (loginTime) {
        const timeDiff = Date.now() - parseInt(loginTime);
        if (timeDiff > 2 * 60 * 60 * 1000) {
          handleLogout();
          setSessionTimeout(true);
        }
      }
    }, 60000); // 每分钟检查一次
    
    return () => clearInterval(interval);
  }, [user]);

  const handleLogin = (userData: User) => {
    setUser(userData);
    setSessionTimeout(false);
    message.success(`欢迎回来，${userData.name}！`);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('loginTime');
    setUser(null);
    navigate('/');
    message.info('已退出登录');
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      Modal.confirm({
        title: '确认退出',
        icon: <ExclamationCircleOutlined />,
        content: '确定要退出登录吗？',
        onOk: handleLogout,
      });
    } else if (key === 'profile') {
      message.info('个人设置功能开发中...');
    } else if (key === 'settings') {
      navigate('/settings');
    }
  };

  // 如果没有登录，显示登录页面
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  // 获取用户可访问的页面
  const accessiblePages = getAccessiblePages(user);
  
  const allMenuItems = [
    {
      key: '/dashboard',
      icon: <BarChartOutlined />,
      label: '仪表盘',
    },
    {
      key: 'business',
      icon: <RobotOutlined />,
      label: '业务管理',
      children: [
        {
          key: '/memory',
          icon: <DatabaseOutlined />,
          label: '记忆管理',
        },
        {
          key: '/profile',
          icon: <UserSwitchOutlined />,
          label: '用户画像',
        },
        {
          key: '/script',
          icon: <MessageOutlined />,
          label: '话术管理',
        },
        {
          key: '/agent',
          icon: <RobotOutlined />,
          label: '智能体管理',
        },
        {
          key: '/knowledge',
          icon: <BookOutlined />,
          label: '知识库管理',
        },
        {
          key: '/workflow',
          icon: <ApartmentOutlined />,
          label: '工作流管理',
        },
      ]
    },
    {
      key: 'operation',
      icon: <BugOutlined />,
      label: '运营管理',
      children: [
        {
          key: '/debug',
          icon: <BugOutlined />,
          label: '调试工具',
        },
        {
          key: '/model',
          icon: <ApiOutlined />,
          label: '大模型管理',
        },
        {
          key: '/reports',
          icon: <BarChartOutlined />,
          label: '数据报表',
        },
      ]
    },
    {
      key: 'system',
      icon: <SettingOutlined />,
      label: '系统管理',
      children: [
        {
          key: '/audit-logs',
          icon: <SecurityScanOutlined />,
          label: '审计日志',
        },
        {
          key: '/operation-logs',
          icon: <ExclamationCircleOutlined />,
          label: '操作日志',
        },
        {
          key: '/account',
          icon: <UserOutlined />,
          label: '账号管理',
        },
        {
          key: '/role',
          icon: <SecurityScanOutlined />,
          label: '角色管理',
        },
        {
          key: '/menu',
          icon: <MenuOutlined />,
          label: '菜单管理',
        },
        {
          key: '/settings',
          icon: <SettingOutlined />,
          label: '系统设置',
        },
      ]
    },
    {
      key: 'legacy',
      icon: <DatabaseOutlined />,
      label: '老平台后台',
      children: [
        {
          key: '/domain',
          icon: <GlobalOutlined />,
          label: '域名管理',
        },
        {
          key: '/activity-rewards',
          icon: <GiftOutlined />,
          label: '活动奖励记录',
        },
        {
          key: '/balance-logs',
          icon: <DollarOutlined />,
          label: '资金日志',
        },
        {
          key: '/card-config',
          icon: <AppstoreOutlined />,
          label: '卡片管理',
        },
        {
          key: '/retention-report',
          icon: <BarChartOutlined />,
          label: '留存报表',
        },
      ]
    },
  ];

  // 根据权限过滤菜单
  const filterMenuItems = (items: any[]): any[] => {
    // 定义为免权限检查的页面
    const openPages = ['domain', 'activity-rewards', 'balance-logs', 'card-config', 'retention-report'];
    
    return items.map(item => {
      if (item.children) {
        // 过滤子菜单
        const filteredChildren = item.children.filter((child: any) => {
          const page = child.key.substring(1); // 移除开头的 '/'
          // 老平台后台页面直接显示
          if (openPages.includes(page)) {
            return true;
          }
          return accessiblePages.includes(page);
        });
        
        // 如果子菜单为空，则不显示父菜单
        if (filteredChildren.length === 0) {
          return null;
        }
        
        return {
          ...item,
          children: filteredChildren
        };
      } else {
        // 直接检查页面权限
        const page = item.key.substring(1); // 移除开头的 '/'
        // 老平台后台页面直接显示
        if (openPages.includes(page)) {
          return item;
        }
        return accessiblePages.includes(page) ? item : null;
      }
    }).filter(Boolean);
  };

  const menuItems = filterMenuItems(allMenuItems);

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人设置',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '系统设置',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLayoutToggle = () => {
    setLayoutMode(prev => prev === 'sidebar' ? 'top' : 'sidebar');
    message.info(`已切换到${layoutMode === 'sidebar' ? '顶部导航' : '侧边栏'}模式`);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {layoutMode === 'sidebar' ? (
        <>
          <Sider trigger={null} collapsible collapsed={collapsed} style={{ overflow: 'hidden' }}>
            <div style={{ 
              height: '100vh', 
              display: 'flex', 
              flexDirection: 'column',
              background: '#001529'
            }}>
              <div style={{ 
                height: 32, 
                margin: 16, 
                background: 'rgba(255, 255, 255, 0.2)', 
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                flexShrink: 0
              }}>
                {collapsed ? 'AI' : 'AI机器人系统'}
              </div>
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                overflowX: 'hidden'
              }}>
                <Menu
                  theme="dark"
                  mode="inline"
                  selectedKeys={[location.pathname]}
                  items={menuItems}
                  onClick={handleMenuClick}
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          </Sider>
          <Layout>
            <Header style={{ padding: 0, background: '#fff' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                height: '100%',
                padding: '0 24px'
              }}>
                <div>
                  {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                    className: 'trigger',
                    onClick: () => setCollapsed(!collapsed),
                    style: { fontSize: '18px', cursor: 'pointer' }
                  })}
                </div>
                <div>
                  <Space>
                    <Button
                      icon={layoutMode === 'sidebar' ? <AppstoreOutlined /> : <MenuOutlined />}
                      onClick={handleLayoutToggle}
                      title="切换布局"
                    />
                    <Dropdown
                      menu={{
                        items: userMenuItems,
                        onClick: handleUserMenuClick,
                      }}
                      placement="bottomRight"
                    >
                      <Space style={{ cursor: 'pointer' }}>
                        <Avatar src={user.avatar} icon={<UserOutlined />} />
                        <Text strong>{user.name}</Text>
                      </Space>
                    </Dropdown>
                  </Space>
                </div>
              </div>
            </Header>
            <Content style={{ 
              height: '100vh', 
              overflowY: 'auto', 
              overflowX: 'hidden',
              background: '#f0f2f5'
            }}>
          <Routes>
            <Route path="/" element={
              <PermissionRoute user={user} page="dashboard" action="view">
                <Dashboard />
              </PermissionRoute>
            } />
            <Route path="/dashboard" element={
              <PermissionRoute user={user} page="dashboard" action="view">
                <Dashboard />
              </PermissionRoute>
            } />
            <Route path="/memory" element={
              <PermissionRoute user={user} page="memory" action="view">
                <MemoryManagement />
              </PermissionRoute>
            } />
            <Route path="/profile" element={
              <PermissionRoute user={user} page="profile" action="view">
                <UserProfile />
              </PermissionRoute>
            } />
            <Route path="/script" element={
              <PermissionRoute user={user} page="script" action="view">
                <ScriptManagement />
              </PermissionRoute>
            } />
            <Route path="/agent" element={
              <PermissionRoute user={user} page="agent" action="view">
                <AgentManagement />
              </PermissionRoute>
            } />
            <Route path="/knowledge" element={
              <PermissionRoute user={user} page="knowledge" action="view">
                <KnowledgeBase />
              </PermissionRoute>
            } />
            <Route path="/workflow" element={
              <PermissionRoute user={user} page="workflow" action="view">
                <WorkflowManagement />
              </PermissionRoute>
            } />
            <Route path="/debug" element={
              <PermissionRoute user={user} page="debug" action="view">
                <DebugTools />
              </PermissionRoute>
            } />
            <Route path="/model" element={
              <PermissionRoute user={user} page="model" action="view">
                <ModelManagement />
              </PermissionRoute>
            } />
            <Route path="/reports" element={
              <PermissionRoute user={user} page="reports" action="view">
                <Reports />
              </PermissionRoute>
            } />
            <Route path="/audit-logs" element={
              <PermissionRoute user={user} page="audit" action="view">
                <AuditLogs />
              </PermissionRoute>
            } />
            <Route path="/operation-logs" element={
              <PermissionRoute user={user} page="audit" action="view">
                <OperationLogs />
              </PermissionRoute>
            } />
            <Route path="/account" element={
              <PermissionRoute user={user} page="account" action="view">
                <AccountManagement />
              </PermissionRoute>
            } />
            <Route path="/role" element={
              <PermissionRoute user={user} page="role" action="view">
                <RoleManagement />
              </PermissionRoute>
            } />
            <Route path="/menu" element={
              <PermissionRoute user={user} page="menu" action="view">
                <MenuManagement />
              </PermissionRoute>
            } />
            <Route path="/settings" element={
              <PermissionRoute user={user} page="settings" action="view">
                <SystemSettings />
              </PermissionRoute>
            } />
            <Route path="/domain" element={
              <DomainManagement />
            } />
            <Route path="/activity-rewards" element={
              <ActivityRewards />
            } />
            <Route path="/balance-logs" element={
              <BalanceLogs />
            } />
            <Route path="/card-config" element={
              <CardManagement />
            } />
            <Route path="/retention-report" element={
              <RetentionReports />
            } />
          </Routes>
            </Content>
          </Layout>
        </>
      ) : (
        // 顶部导航布局
        <Layout>
          <Header style={{ 
            padding: 0, 
            background: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              height: '100%',
              padding: '0 24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ 
                  fontSize: '20px', 
                  fontWeight: 'bold', 
                  color: '#1890ff',
                  marginRight: 24
                }}>
                  AI机器人系统
                </div>
                <Menu
                  mode="horizontal"
                  selectedKeys={[location.pathname]}
                  items={menuItems}
                  onClick={handleMenuClick}
                  style={{ border: 'none', background: 'transparent' }}
                />
              </div>
              <div>
                <Space>
                  <Button
                    icon={<MenuOutlined />}
                    onClick={handleLayoutToggle}
                    title="切换到侧边栏模式"
                  />
                  <Dropdown
                    menu={{
                      items: userMenuItems,
                      onClick: handleUserMenuClick,
                    }}
                    placement="bottomRight"
                  >
                    <Space style={{ cursor: 'pointer' }}>
                      <Avatar src={user.avatar} icon={<UserOutlined />} />
                      <Text strong>{user.name}</Text>
                    </Space>
                  </Dropdown>
                </Space>
              </div>
            </div>
          </Header>
          <Content style={{ 
            height: 'calc(100vh - 64px)', 
            overflowY: 'auto', 
            overflowX: 'hidden',
            background: '#f0f2f5'
          }}>
            <Routes>
              <Route path="/" element={
                <PermissionRoute user={user} page="dashboard" action="view">
                  <Dashboard />
                </PermissionRoute>
              } />
              <Route path="/dashboard" element={
                <PermissionRoute user={user} page="dashboard" action="view">
                  <Dashboard />
                </PermissionRoute>
              } />
              <Route path="/memory" element={
                <PermissionRoute user={user} page="memory" action="view">
                  <MemoryManagement />
                </PermissionRoute>
              } />
              <Route path="/profile" element={
                <PermissionRoute user={user} page="profile" action="view">
                  <UserProfile />
                </PermissionRoute>
              } />
              <Route path="/script" element={
                <PermissionRoute user={user} page="script" action="view">
                  <ScriptManagement />
                </PermissionRoute>
              } />
              <Route path="/agent" element={
                <PermissionRoute user={user} page="agent" action="view">
                  <AgentManagement />
                </PermissionRoute>
              } />
              <Route path="/knowledge" element={
                <PermissionRoute user={user} page="knowledge" action="view">
                  <KnowledgeBase />
                </PermissionRoute>
              } />
              <Route path="/workflow" element={
                <PermissionRoute user={user} page="workflow" action="view">
                  <WorkflowManagement />
                </PermissionRoute>
              } />
              <Route path="/debug" element={
                <PermissionRoute user={user} page="debug" action="view">
                  <DebugTools />
                </PermissionRoute>
              } />
              <Route path="/model" element={
                <PermissionRoute user={user} page="model" action="view">
                  <ModelManagement />
                </PermissionRoute>
              } />
              <Route path="/reports" element={
                <PermissionRoute user={user} page="reports" action="view">
                  <Reports />
                </PermissionRoute>
              } />
              <Route path="/audit-logs" element={
                <PermissionRoute user={user} page="audit" action="view">
                  <AuditLogs />
                </PermissionRoute>
              } />
              <Route path="/operation-logs" element={
                <PermissionRoute user={user} page="audit" action="view">
                  <OperationLogs />
                </PermissionRoute>
              } />
              <Route path="/account" element={
                <PermissionRoute user={user} page="account" action="view">
                  <AccountManagement />
                </PermissionRoute>
              } />
              <Route path="/role" element={
                <PermissionRoute user={user} page="role" action="view">
                  <RoleManagement />
                </PermissionRoute>
              } />
              <Route path="/menu" element={
                <PermissionRoute user={user} page="menu" action="view">
                  <MenuManagement />
                </PermissionRoute>
              } />
              <Route path="/settings" element={
                <PermissionRoute user={user} page="settings" action="view">
                  <SystemSettings />
                </PermissionRoute>
              } />
              <Route path="/domain" element={
                <DomainManagement />
              } />
              <Route path="/activity-rewards" element={
                <ActivityRewards />
              } />
              <Route path="/balance-logs" element={
                <BalanceLogs />
              } />
              <Route path="/card-config" element={
                <CardManagement />
              } />
            </Routes>
          </Content>
        </Layout>
      )}
    </Layout>
  );
};

export default App;